<template>
  <transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-md"
        @click="close"
      ></div>

      <!-- Modal Content -->
      <div
        class="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-10 animate-scale-up custom-scrollbar"
      >
        <!-- Header -->
        <div class="text-center mb-10">
          <h2
            class="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-md"
          >
            {{ t("Unlock Full Potential") }}
          </h2>
          <p class="text-lg text-white/80 max-w-2xl mx-auto">
            {{ t("Choose the perfect plan for your wedding journey. Upgrade anytime as your needs grow.") }}
          </p>
          <button
            @click="close"
            class="absolute top-6 right-6 text-white/60 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Plans Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="(plan, index) in allPlans"
            :key="index"
            class="relative group rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-2 flex flex-col"
            :class="[
              plan.popular
                ? 'bg-gradient-to-br from-indigo-500/20 to-purple-600/20 border-indigo-400/50 shadow-[0_0_20px_rgba(99,102,241,0.2)]'
                : 'bg-white/5 border-white/10 hover:border-white/20',
              plan.id === userSubscription?.id
                ? 'ring-2 ring-indigo-500 scale-[1.02]'
                : '',
            ]"
          >
            <div
              v-if="plan.popular"
              class="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg uppercase tracking-wider"
            >
              {{ t("Most Popular") }}
            </div>

            <div class="mb-4">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-xl font-bold text-white">{{ plan.name }}</h3>
                <span
                  class="text-[10px] font-black uppercase tracking-widest text-indigo-300 bg-indigo-500/10 px-2 py-0.5 rounded-md border border-indigo-500/20"
                >
                  {{ plan.planDuration ? t(plan.planDuration) : t("Life Time") }}
                </span>
              </div>
              <div class="flex items-baseline gap-1 text-white">
                <span class="text-3xl font-black">${{ plan.price }}</span>
                <span class="text-sm text-white/40 font-medium">
                  {{
                    plan.planDuration === "monthly"
                      ? "/ mo"
                      : plan.planDuration === "yearly"
                      ? "/ yr"
                      : "one-time"
                  }}
                </span>
              </div>
            </div>

            <p class="text-sm text-white/70 mb-6 min-h-[40px] leading-relaxed">
              {{ plan.description }}
            </p>

            <div class="flex-1 space-y-3 mb-8">
              <div
                v-for="(feature, fIndex) in getPlanFeatures(plan)"
                :key="fIndex"
                class="flex items-start gap-3 text-sm text-white/90 group/item"
              >
                <div
                  class="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500/20 transition-colors"
                >
                  <svg
                    class="w-3 h-3 text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2.5"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <span class="font-medium">{{ feature }}</span>
              </div>
            </div>

            <button
              class="w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 shadow-xl mt-auto relative overflow-hidden group/btn"
              :class="[
                plan.id === userSubscription?.id && !isCurrentPlanExpired
                  ? 'bg-indigo-500/50 text-white cursor-default border border-indigo-400/30'
                  : plan.popular
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:scale-[1.02] hover:shadow-indigo-500/25'
                    : 'bg-white/10 text-white hover:bg-white/20 hover:scale-[1.02] border border-white/20',
              ]"
              :disabled="
                (plan.id === userSubscription?.id && !isCurrentPlanExpired) || showQR
              "
              @click="handleSelectPlan(plan)"
            >
              <span class="relative z-10">
                {{
                  plan.id === userSubscription?.id
                    ? isCurrentPlanExpired
                      ? t("Renew Plan")
                      : t("Current Plan")
                    : plan.cta ? t(plan.cta) : t("Get Started")
                }}
              </span>
              <div
                v-if="plan.popular && plan.id !== userSubscription?.id"
                class="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-500 opacity-0 group-hover/btn:opacity-100 transition-opacity"
              ></div>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-12 text-center">
          <p class="text-white/40 text-sm font-medium tracking-wide">
            <i class="fa-solid fa-shield-halved mr-2 text-indigo-400/50"></i>
            {{ t("Secure payment. All plans include 24/7 priority support.") }}
          </p>
        </div>
      </div>

      <!-- Payment Overlay (Moved outside main modal content scroll area to ensure it blocks events correctly) -->
      <transition name="fade">
        <div
          v-if="showQR"
          class="fixed inset-0 z-[110] flex items-center justify-center p-3 md:p-4 bg-black/80 backdrop-blur-sm"
        >
          <div
            class="relative bg-[#1a1a1a] rounded-3xl p-6 md:p-8 max-w-md w-full border border-white/10 shadow-2xl animate-scale-up"
          >
            <div class="text-center mb-6">
              <h3 class="text-xl md:text-2xl font-bold text-white mb-2">
                {{ t("Scan to Pay") }}
              </h3>
              <p class="text-white/60 text-sm">
                {{ t("Please scan the QR code to complete your subscription for") }}
                <span class="text-indigo-400 font-bold">{{
                  selectedPlan?.name
                }}</span>
              </p>
            </div>

            <div
              class="bg-white p-3 md:p-4 rounded-2xl mb-6 flex justify-center overflow-hidden"
            >
              <custom-k-h-q-r
                :merchant-name="'Chamrouen PichSamphors'"
                :amount="parseFloat(selectedPlan?.price || 0)"
                currency="KHR"
                merchant-id="pichsamphors_chamroeun@aclb"
                merchant-city="Battambang"
                country-code="KH"
                :bill-number="'SUB-' + Date.now()"
                mobile-number="85587344479"
                store-label="Wedding Ledger"
                terminal-label="WEB-01"
                :expireTime="props.expireTime || 3"
                @success="handlePaymentSuccess"
              />
            </div>

            <div class="text-center">
              <div
                class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white mb-6"
              >
                <div
                  class="w-2 h-2 rounded-full bg-red-500 animate-pulse"
                ></div>
                <span class="text-sm font-mono tracking-wider"
                  >{{ t("Expires in:") }} {{ formatTime(timeLeft) }}</span
                >
              </div>

              <button
                @click="cancelPayment"
                class="w-full py-3 rounded-xl font-bold text-white/60 hover:text-white hover:bg-white/5 transition-colors border border-white/10"
              >
                {{ t("Cancel Payment") }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>

  <component
    :is="currentComponent"
    :visible="openForm"
    :isFailed="isFailed"
    :informMessage="informMessage"
    :showToast="showToast"
    @onClose="handleListenEmitEvent"
  />
</template>

<script setup>
import { computed, defineProps, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { getCollectionQuery } from "@/composables/getCollection";
import { projectAuth, projectFirestore } from "@/firebase/config";
import {
  documentId,
  orderBy,
  where,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

const { t } = useI18n();

import CustomKHQR from "../CustomKHQR.vue";
import useCollection from "@/composables/useDocument";
import ToastedVue from "../ToastedVue.vue";
import  { calculateSubscriptionEnd ,calculateExpirePlan} from "@/composables/calculateExpirePlan";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  onClose: {
    type: Function,
    default: () => {},
  },
});

const close = () => {
  props.onClose();
};

const allPlans = ref([]);
const currentComponent = ref(null);
const informMessage = ref("");
const openForm = ref(false);
const showToast = ref(false);
const isFailed = ref(false);
const timeStamp = serverTimestamp();

const getPlanFeatures = (plan) => {
  const durationMap = {
    monthly: t("Monthly Access"),
    yearly: t("Yearly Access"),
    lifetime: t("Life Time Access"),
  };

  const features = [durationMap[plan.planDuration] || t("Life Time Access")];

  // Dynamic features based on plan properties
  if (plan.maxEvent !== undefined) {
    features.push(
      `${plan.maxEvent} ${plan.maxEvent > 1 ? t("Events Allowed") : t("Event Allowed")}`
    );
  }
  if (plan.maxGuest !== undefined) {
    features.push(t("Up to {count} Guests", { count: plan.maxGuest }));
  }
  if (plan.maxExpense !== undefined) {
    features.push(t("Up to {count} Expenses", { count: plan.maxExpense }));
  }

  // Add some default features if it's a paid plan or based on descriptions
  if (plan.price > 0) {
    features.push(t("Priority Support"));
    features.push(t("Advanced Analytics"));
  }

  return features;
};

const fetchAllPlans = async () => {
  try {
    await getCollectionQuery(
      "plans",
      [where("status", "==", true), orderBy("price", "asc")],
      (data) => {
        allPlans.value = data;
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};

const userByID = ref([]);
const fetchUserById = async () => {
  try {
    await getCollectionQuery(
      "users",
      [where(documentId(), "==", projectAuth.currentUser?.uid)],
      (data) => {
        userByID.value = data;
      },
      null,
    true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};

const userSubscription = computed(() => {
  return allPlans.value.find((plan) => plan.id === userByID.value[0]?.planId);
});

const isCurrentPlanExpired = computed(() => {
  const user = userByID.value[0];
  const plan = userSubscription.value;

  if (user?.subscriptionEnd && plan?.planType) {
    return calculateExpirePlan(
      user.subscriptionEnd,
      plan.planType
    );
  }
  return false;
});

// Payment Logic
const selectedPlan = ref(null);
const showQR = ref(false);
const timeLeft = ref(180);
let timerInterval = null;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const startTimer = () => {
  if (timerInterval) clearInterval(timerInterval);
  timeLeft.value = 180;
  timerInterval = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--;
    } else {
      cancelPayment();
    }
  }, 1000);
};

const handleSelectPlan = async (plan) => {
  const isCurrentPlan = plan.id === userSubscription.value?.id;

  if ((isCurrentPlan && !isCurrentPlanExpired.value) || showQR.value) return;
  selectedPlan.value = plan;
  
  // Defensive check for free plan (case-insensitive and type-safe)
  const isFreePrice = parseFloat(plan.price) === 0;
  const isFreeType = plan.planType?.toLowerCase() === 'free';

  if (isFreePrice || isFreeType) {
    console.log("Free plan detected, updating directly...");
    await updateSubscriptionDirectly(plan);
  } else {
    showQR.value = true;
    startTimer();
  }
};

const updateSubscriptionDirectly = async (plan) => {
  try {
    const userId = projectAuth.currentUser?.uid;
    const planId = plan.id;

    const { updateDocs } = useCollection("users");
    const newDate = new Date();
    
    const endDate = calculateSubscriptionEnd(newDate, plan.planDuration, plan.planType);
    console.log("endDate", endDate);

    
    
    await updateDocs(userId, { planId, subscriptionStart: timeStamp, subscriptionEnd: plan.planDuration === 'lifetime' || plan.planType === 'free' ? timeStamp : endDate });

    selectedPlan.value = null;
    handleListenEmitEvent("upgrade");
  } catch (error) {
    console.error("Error updating free subscription:", error);
    isFailed.value = true;
    informMessage.value = t("Failed to update subscription. Please contact support.");
    showToast.value = true;
    currentComponent.value = ToastedVue;
  }
};

const cancelPayment = () => {
  showQR.value = false;
  selectedPlan.value = null;
  if (timerInterval) clearInterval(timerInterval);
};

onMounted(async () => {
  await fetchUserById();
  fetchAllPlans();
});

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
});

const handlePaymentSuccess = async (data) => {
  if (!selectedPlan.value) return;

  try {
    // Optional UX delay (keep if CustomKHQR needs it)
    await new Promise(resolve => setTimeout(resolve, 2000));

    const userId = projectAuth.currentUser?.uid;
    const planId = selectedPlan.value.id;

    const { updateDocs } = useCollection("users");
    const { addDocs } = useCollection("bakong-transactions");


    const requestData = {
      userId,
      planId,
      data: data,
      createdAt: timeStamp,
    };

    const newDate = new Date();
    const endDate = calculateSubscriptionEnd(newDate, selectedPlan.value.planDuration, selectedPlan.value.planType);

      console.log("endDate", endDate);

    // 🔥 Run DB writes in parallel
    await Promise.all([
      updateDocs(userId, { planId,subscriptionStart: timeStamp,subscriptionEnd: selectedPlan.value.planDuration === 'lifetime' || selectedPlan.value.planType === 'free' ? timeStamp : endDate }),
      addDocs(requestData),
    ]);


    // UI cleanup
    showQR.value = false;
    selectedPlan.value = null;

    handleListenEmitEvent("upgrade");
  } catch (error) {
    console.error("Error updating subscription:", error);

    isFailed.value = true;
    informMessage.value =
      t("Failed to update subscription. Please contact support.");
    showToast.value = true;
    openForm.value = false;
    currentComponent.value = ToastedVue;
  }
};


const handleListenEmitEvent = (status) => {
  if (status == "upgrade") {
    informMessage.value = t("Subscription upgraded successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;

    currentComponent.value = ToastedVue;
    showToast.value = true;
  } else if (status == "close") {
    console.log("part close");
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = "";
  } else {
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;

    currentComponent.value = "";
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Custom Scrollbar for Modal content */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
